import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Search, ChevronRight, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { CATEGORIES, INTENTS } from "@/constants";
import { getGeo } from "@/lib/filters";
import PlaceCard from "@/components/PlaceCard";

function IntentChip({ intent }) {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const Icon = Icons[intent.icon] || Icons.Sparkles;
  const go = async () => {
    const params = new URLSearchParams();
    Object.entries(intent.params).forEach(([k, v]) => params.set(k, String(v)));
    if (intent.maxDuration) params.set("md", String(intent.maxDuration));
    params.set("title", intent.label[lang] || intent.label.fr);
    navigate(`/explore?${params.toString()}`);
  };
  return (
    <button
      data-testid={`intent-${intent.key}`}
      onClick={go}
      className="group flex shrink-0 items-center gap-2 rounded-2xl border border-border bg-white px-4 py-3 shadow-sm transition-all hover:border-terracotta hover:shadow-md"
    >
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-terracotta-light text-terracotta">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="text-sm font-semibold text-ink whitespace-nowrap">{localized(intent.label, lang)}</span>
      <ArrowRight className="h-4 w-4 text-ink-muted transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

export default function Discover() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [teamPicks, setTeamPicks] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
    api.get("/places", { params: { team_pick: true, limit: 8 } }).then((r) => setTeamPicks(r.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/explore?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-sea p-6 text-white shadow-lg">
        <div className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-terracotta/40 blur-2xl" />
        <div className="absolute -bottom-12 -left-6 h-40 w-40 rounded-full bg-citrus/30 blur-2xl" />
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-widest text-citrus">València · City Guide</p>
          <h1 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold leading-tight">
            {t("tagline")}
          </h1>
          <form onSubmit={submitSearch} className="mt-5 flex items-center gap-2 rounded-2xl bg-white p-1.5 shadow-md">
            <Search className="ml-2 h-5 w-5 text-ink-muted" />
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="flex-1 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-ink-muted"
            />
            <button data-testid="hero-search-submit" type="submit" className="rounded-xl bg-terracotta px-4 py-2 text-sm font-bold text-white hover:bg-terracotta-hover">
              {t("explore")}
            </button>
          </form>
        </div>
      </section>

      {/* Intents */}
      <section>
        <h2 className="mb-3 font-heading text-lg font-bold text-ink">{t("intents_title")}</h2>
        <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {INTENTS.map((i) => <IntentChip key={i.key} intent={i} />)}
        </div>
      </section>

      {/* Categories */}
      <section>
        <h2 className="mb-3 font-heading text-lg font-bold text-ink">{t("categories_title")}</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {CATEGORIES.map((c) => {
            const Icon = Icons[c.icon] || Icons.MapPin;
            return (
              <button
                key={c.id}
                data-testid={`category-${c.id}`}
                onClick={() => navigate(`/explore?category=${c.id}&title=${encodeURIComponent(localized(c.label, lang))}`)}
                className="group relative overflow-hidden rounded-2xl p-4 text-left text-white shadow-sm transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: c.color }}
              >
                <Icon className="h-6 w-6 opacity-90" />
                <p className="mt-6 font-heading text-lg font-bold">{localized(c.label, lang)}</p>
                <p className="text-xs opacity-80">{localized(c.sub, lang)}</p>
                <ChevronRight className="absolute bottom-4 right-3 h-5 w-5 opacity-70 transition-transform group-hover:translate-x-0.5" />
              </button>
            );
          })}
        </div>
      </section>

      {/* Collections */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-ink">{t("collections_title")}</h2>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {collections.map((col) => (
            <button
              key={col.id}
              data-testid={`collection-${col.slug}`}
              onClick={() => navigate(`/collection/${col.slug}`)}
              className="group relative h-52 w-64 shrink-0 overflow-hidden rounded-3xl text-left shadow-md"
            >
              <img src={col.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                <span className="inline-block rounded-full bg-citrus/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide">{localized(col.badge, lang)}</span>
                <h3 className="mt-2 font-heading text-lg font-extrabold leading-tight">{localized(col.title, lang)}</h3>
                <p className="mt-0.5 text-xs opacity-90 line-clamp-2">{localized(col.subtitle, lang)}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Team picks */}
      {teamPicks.length > 0 && (
        <section>
          <h2 className="mb-3 font-heading text-lg font-bold text-ink">{t("team_picks_title")}</h2>
          <div className="grid grid-cols-2 gap-4">
            {teamPicks.slice(0, 4).map((p) => <PlaceCard key={p.id} place={p} compact />)}
          </div>
        </section>
      )}
    </div>
  );
}
